import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Editor as TinyMce } from '@tinymce/tinymce-react';
import { Button, Space } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';

import FooterActions from '~/components/FooterActions';
import { getStorage, setStorage } from '~/helpers/storage';

const Editor = forwardRef(
  ({ content, disabled, height, readOnly, onSave, setLoading, ...editorProps }, ref) => {
    const [tinyRef, setTinyRef] = useState({});
    const [contrato, setContrato] = useState({});

    const initialRender = useCallback(() => {
      const temp = getStorage('contrato', 'session');
      setContrato(temp);
    }, []);

    useEffect(() => {
      initialRender();
    }, [initialRender]);

    useEffect(() => {
      if (_.isEmpty(tinyRef) || typeof tinyRef.setContent !== 'function') {
        return;
      }

      const nextContent = content ?? '';
      const currentContent =
        typeof tinyRef.getContent === 'function' ? tinyRef.getContent({ format: 'html' }) : '';

      if (currentContent !== nextContent) {
        tinyRef.setContent(nextContent);
      }

      if (!_.isEmpty(contrato) && contrato.template !== nextContent) {
        setContrato((prev) => ({ ...(prev || {}), template: nextContent }));
      }
      // We only want to react to external content changes (ex: async load in Finalizar).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, tinyRef]);

    const handleEditorChange = (value) => {
      const temp = { ...contrato, template: value };
      setContrato(temp);
    };

    const salvarTemplate = () => {
      setStorage('contrato', contrato, 'session');
      onSave(false);
    };

    useImperativeHandle(ref, () => ({
      imprimirTemplate() {
        if (!_.isEmpty(tinyRef)) {
          const previousTitle = document.title;
          const nextTitle = contrato?.nome ? String(contrato.nome) : previousTitle;

          if (nextTitle && nextTitle !== previousTitle) {
            document.title = nextTitle;
          }

          const restoreTitle = () => {
            document.title = previousTitle;
          };

          window.addEventListener('afterprint', restoreTitle, { once: true });

          try {
            tinyRef.execCommand('mcePrint');
          } finally {
            // Fallback in case afterprint doesn't fire consistently.
            window.setTimeout(() => {
              restoreTitle();
            }, 1500);
          }
        }
      },
    }));

    const defaultPlugins = [
      'code advlist autolink lists link image charmap print preview anchor hr',
      'searchreplace visualblocks code fullscreen',
      'insertdatetime media table paste code wordcount',
    ];

    const defaultMenubar = 'file edit view insert format tools table tc';

    const defaultToolbar = [
      'formatselect bold italic underline forecolor',
      'alignleft aligncenter alignright alignjustify bullist numlist outdent indent',
      'link',
      'undo redo',
      'hr fullscreen',
      'code',
    ].join('|');

    const handleFilePicker = (callback) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();
  
        reader.onload = () => {
          callback(reader.result, { title: file.name });
        };
  
        reader.readAsDataURL(file);
      };

      input.click();
    };
    return (
      <div {...editorProps}>
        <TinyMce
          apiKey="tdytazgqvdwe5cv3p4j3s9twvbemexwv77vgfln4wxs2pb2u"
          initialValue={content}
          disabled={disabled}
          init={{
            selector: 'textarea',
            language: 'pt_BR',
            height,
            menubar: defaultMenubar,
            branding: false,
            resize: false,
            plugins: defaultPlugins,
            toolbar: defaultToolbar,
            entity_encoding: 'raw',
            file_picker_callback: handleFilePicker,
          }}
          onInit={(evt, editor) => {
            setLoading({ active: false, text: '' });
            setTinyRef(editor);
          }}
          onEditorChange={handleEditorChange}
          tinymceScriptSrc="https://cdn.e-auditoria.com.br/tinymce/tinymce.min.js"
        />

        {!disabled ? (
          <FooterActions>
            <Space>
              <Button size="large" icon={<CloseOutlined />} onClick={() => onSave(false)}>
                Cancelar
              </Button>

              <Button type="primary" size="large" icon={<SaveOutlined />} onClick={salvarTemplate}>
                Salvar
              </Button>
            </Space>
          </FooterActions>
        ) : null}
      </div>
    );
  }
);

Editor.defaultProps = {
  content: '',
  disabled: false,
  height: 'calc(100vh - 18rem)',
  readOnly: false,
  onSave: () => null,
  setLoading: () => null,
};

Editor.propTypes = {
  content: PropTypes.string,
  disabled: PropTypes.bool,
  height: PropTypes.string,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
  setLoading: PropTypes.func,
};

export default Editor;
